import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Layers, Mail, Mountain, Percent, Shield, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const PRODUCT_CODE = "MINERAL_360";
const UNIT_CENTS = 1990; // R$19,90 (matriz comercial)

// Espelha BATCH_DISCOUNT_TIERS do backend
const tierPercent = (n) => {
  if (n >= 11 && n <= 20) return 30;
  if (n >= 6  && n <= 10) return 20;
  if (n >= 3  && n <= 5)  return 10;
  return 0;
};

const formatBRL = (cents) => (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const parseProcessos = (raw) =>
  raw.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);

export default function ConsultaLoteMineral() {
  const [raw, setRaw] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const processos = useMemo(() => parseProcessos(raw), [raw]);
  const n = processos.length;
  const pct = tierPercent(n);
  const bruto = n * UNIT_CENTS;
  const desconto = Math.floor(bruto * pct / 100);
  const total = bruto - desconto;

  const submit = async (e) => {
    e.preventDefault();
    if (n < 2) { toast.error("Informe pelo menos 2 processos para consulta em lote"); return; }
    if (n > 20) { toast.error("Máximo 20 processos por lote"); return; }
    if (!email) { toast.error("Informe seu e-mail"); return; }
    setLoading(true);
    try {
      const q = await axios.post(`${API}/api/mineral/orders/quote`, {
        guest_email: email.trim(),
        product_code: PRODUCT_CODE,
        quantity: 1, // será sobrescrito pelo backend com n
        delivery_channel: "email",
        delivery_target: email.trim(),
        criteria: { processos },
      });
      const c = await axios.post(`${API}/api/mineral/orders/${q.data.order_id}/checkout`);
      window.location.href = c.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar pedido em lote");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #050810 0%, #0A1730 40%, #0B1B2F 100%)" }}
         data-testid="consulta-lote-mineral">
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-6 flex items-center justify-between">
        <Link to="/consulta-mineral" className="flex items-center gap-2 text-white/80 hover:text-white transition text-sm">
          <span aria-hidden="true">←</span> Voltar
        </Link>
        <div className="text-[10px] tracking-[.3em] uppercase font-semibold" style={{ color: "#F0D37A" }}>
          Hermes · Consulta em Lote
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 pb-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-amber-400/30"
               style={{ background: "rgba(212, 175, 55, 0.08)", color: "#F0D37A" }}>
            <Layers className="w-3.5 h-3.5"/>
            <span className="text-[11px] uppercase tracking-widest font-semibold">Até 20 processos · 1 relatório</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white leading-tight">
            Consulta <span style={{ color: "#D4AF37" }}>em lote</span> — economia progressiva
          </h1>
          <p className="mt-4 text-neutral-300/80 max-w-xl mx-auto text-base leading-relaxed">
            Cole até 20 números de processo, um por linha (ou separados por vírgula).
            A LUNA gera uma análise consolidada e você recebe UM relatório com tudo.
          </p>
        </div>

        {/* Tabela de desconto */}
        <div className="grid grid-cols-4 gap-2 mb-6" data-testid="tiers-tabela">
          {[
            { r: "1-2",   d: 0  },
            { r: "3-5",   d: 10 },
            { r: "6-10",  d: 20 },
            { r: "11-20", d: 30 },
          ].map((t) => {
            const active = n >= 1 && (
              (t.r === "1-2" && n <= 2) ||
              (t.r === "3-5" && n >= 3 && n <= 5) ||
              (t.r === "6-10" && n >= 6 && n <= 10) ||
              (t.r === "11-20" && n >= 11)
            );
            return (
              <div key={t.r} className={`rounded-xl p-3 text-center border ${active ? "border-amber-400/60 shadow-lg" : "border-white/10"}`}
                   style={{ background: active ? "rgba(212,175,55,.12)" : "rgba(255,255,255,.03)" }}>
                <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: active ? "#F0D37A" : "#94a3b8" }}>
                  {t.r} proc.
                </div>
                <div className="mt-1 font-heading text-xl font-bold" style={{ color: active ? "#F5DE8C" : "#e2e8f0" }}>
                  {t.d === 0 ? "cheio" : `-${t.d}%`}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={submit} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <label className="text-[11px] uppercase tracking-widest font-semibold text-white/60">
            Cole os números dos processos (um por linha)
          </label>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)}
            data-testid="textarea-processos"
            placeholder={`880954/1985\n880002/1992\n880003/1992`}
            rows={6}
            className="mt-2 w-full p-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/30 font-mono text-sm focus:outline-none focus:border-amber-400/60"/>
          <div className="mt-1 text-[11px] text-white/40 flex items-center justify-between">
            <span>{n} processo{n === 1 ? "" : "s"} detectado{n === 1 ? "" : "s"}</span>
            {n > 20 && <span className="text-rose-300">Máx: 20</span>}
          </div>

          <label className="mt-5 block text-[11px] uppercase tracking-widest font-semibold text-white/60">
            Seu e-mail
          </label>
          <div className="relative mt-2">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"/>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              data-testid="input-lote-email"
              placeholder="advogado@escritorio.com"
              className="w-full h-12 pl-10 pr-4 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-amber-400/60"/>
          </div>

          {/* Preço */}
          <div className="mt-6 p-4 rounded-xl border border-white/10 bg-white/5" data-testid="lote-preco-preview">
            <div className="flex items-center justify-between text-sm text-white/60">
              <span>{n} processo{n === 1 ? "" : "s"} × {formatBRL(UNIT_CENTS)}</span>
              <span>{formatBRL(bruto)}</span>
            </div>
            {pct > 0 && (
              <div className="mt-1 flex items-center justify-between text-sm text-amber-300/90">
                <span className="inline-flex items-center gap-1"><Percent className="w-3 h-3"/> Desconto {pct}%</span>
                <span>-{formatBRL(desconto)}</span>
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-widest font-semibold text-white/50">Total</span>
              <span className="font-heading text-2xl font-bold" style={{ color: "#F5DE8C" }}>{formatBRL(total)}</span>
            </div>
          </div>

          <button type="submit" disabled={loading || n < 2 || n > 20}
            data-testid="btn-consultar-lote"
            className="mt-6 w-full h-12 rounded-xl font-semibold text-base transition shadow-lg disabled:opacity-40"
            style={{ background: "linear-gradient(180deg, #F5DE8C 0%, #D4AF37 100%)", color: "#0B1B2F" }}>
            {loading ? "Aguarde…" : `Consultar ${n || 0} processos →`}
          </button>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
            <div className="flex items-start gap-2 text-white/60">
              <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#D4AF37" }}/>
              <span>Pagamento antes do processamento — nada é gerado antes.</span>
            </div>
            <div className="flex items-start gap-2 text-white/60">
              <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#D4AF37" }}/>
              <span>Relatório consolidado disponível por 10 dias.</span>
            </div>
            <div className="flex items-start gap-2 text-white/60">
              <Mountain className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#D4AF37" }}/>
              <span>Base ANM oficial + análise comparativa LUNA.</span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
